import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Col, FormLabel, Row } from "react-bootstrap";
import moment from "moment";
// Mesma linguagem visual dos bloqueios de agenda (cards, células, selos): as classes
// block-list__* vêm de lá em vez de serem copiadas para um segundo arquivo de estilo.
import "./AppointmentBlockList.css";
import Api from "../../../constants/Api";
import Common from "../../../constants/Common";
import axiosInstance from "../../../services/AxiosInstance";
import { errorObjectAlert, successAlert, warnAlert, warnAlertWithReason } from "../../components/alert/defaultAlert";
import CustomSelect from "../../components/Forms/Element/CustomSelect";
import InputDate from "../../components/Forms/Element/InputDate";
import InputText from "../../components/Forms/Element/InputText";
import SegmentedButtons from "../../components/Forms/Element/SegmentedButtons";
import FilterAccordion from "../../components/Common/Filter/FilterAccordion";
import GridCustomBase from "../../components/GridCustom/GridCustomBase";
import PageTitle from "../../layouts/PageTitle";

/**
 * Clientes sem acesso ao agendamento online por falta ou por cancelamento em cima da hora,
 * com a liberação manual e o histórico de quem liberou quem.
 *
 * A penalidade não é um cadastro: o backend a recalcula a cada tentativa de agendar. Liberar
 * grava um marco de data e hora, e só faltas e cancelamentos posteriores a ele voltam a
 * bloquear o cliente.
 */

const MODE_BLOCKED = "BLOCKED";
const MODE_RELEASES = "RELEASES";
const TYPE_NO_SHOW = "NO_SHOW";
const TYPE_LATE_CANCELLATION = "LATE_CANCELLATION";

const DEFAULT_PAGE_SIZE = Common.DEFAULT_ROWS_PER_PAGE;

/** Dia de hoje menos N dias, no início do dia. */
const daysAgo = (days) => moment().subtract(days, "days").startOf("day").toDate();

const BlockedCustomerList = () => {
    const { t } = useTranslation();

    const [mode, setMode] = useState(MODE_BLOCKED);

    // O histórico abre nos últimos 30 dias: é consulta de "quem liberou este cliente",
    // e varrer a vida toda por padrão só deixaria a primeira carga lenta.
    const [startDate, setStartDate] = useState(daysAgo(30));
    const [endDate, setEndDate] = useState(new Date());
    const [typeFilter, setTypeFilter] = useState(null);
    const [nameFilter, setNameFilter] = useState("");
    // Filtros aplicados só no Buscar, como nas demais listagens.
    const [applied, setApplied] = useState({ type: null, name: "" });

    const [blocked, setBlocked] = useState([]);
    const [releases, setReleases] = useState([]);
    const [pageable, setPageable] = useState({});
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
    const [showLoading, setShowLoading] = useState(true);

    const typeOptions = [
        { value: TYPE_NO_SHOW, label: t("blocked_by_no_show") },
        { value: TYPE_LATE_CANCELLATION, label: t("blocked_by_late_cancellation") }
    ];

    const modeOptions = [
        { value: MODE_BLOCKED, label: t("blocked_now"), icon: "fa-light fa-lock" },
        { value: MODE_RELEASES, label: t("online_release_history"), icon: "fa-light fa-clock-rotate-left" }
    ];

    const loadBlocked = useCallback(() => {
        setShowLoading(true);
        axiosInstance.get(Api.appointment.onlinePenalty)
            .then((response) => setBlocked(Array.isArray(response.data) ? response.data : []))
            .catch((error) => {
                errorObjectAlert(error);
                setBlocked([]);
            })
            .finally(() => setShowLoading(false));
    }, []);

    const loadReleases = useCallback((targetPage, targetSize) => {
        if (!startDate || !endDate) {
            warnAlert(t("period_filter_requires_both_dates"));
            return;
        }
        setShowLoading(true);
        axiosInstance.get(`${Api.appointment.onlinePenalty}/releases`, {
            params: {
                startDate: moment(startDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                endDate: moment(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                page: targetPage,
                size: targetSize
            }
        })
            .then((response) => {
                setPageable(response.data || {});
                setReleases(Array.isArray(response.data?.content) ? response.data.content : []);
            })
            .catch((error) => {
                errorObjectAlert(error);
                setReleases([]);
            })
            .finally(() => setShowLoading(false));
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [startDate, endDate]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (mode === MODE_BLOCKED) {
            loadBlocked();
        } else {
            loadReleases(page, size);
        }
    }, [mode, page, size]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const handleModeChange = (nextMode) => {
        setPage(0);
        setMode(nextMode);
    };

    const handleSearch = () => {
        setApplied({ type: typeFilter, name: nameFilter });
        if (mode === MODE_BLOCKED) {
            setPage(0);
            loadBlocked();
            return;
        }
        if (page !== 0) {
            setPage(0);
            return;
        }
        loadReleases(0, size);
    };

    const handleClearFilters = () => {
        setStartDate(daysAgo(30));
        setEndDate(new Date());
        setTypeFilter(null);
        setNameFilter("");
        setApplied({ type: null, name: "" });
    };

    // Telefone sem pontuação também casa: a recepção costuma digitar só os números.
    const matchesName = (row, term) => {
        const normalized = term.trim().toLowerCase();
        if (!normalized) {
            return true;
        }
        const digits = normalized.replace(/\D/g, "");
        return (row.customerName || "").toLowerCase().includes(normalized)
            || (digits && (row.customerPhone || "").replace(/\D/g, "").includes(digits));
    };

    const filteredBlocked = useMemo(
        () => blocked.filter((row) => (!applied.type?.value || row.type === applied.type.value)
            && matchesName(row, applied.name)),
        [blocked, applied]
    );

    const filteredReleases = useMemo(
        () => releases.filter((row) => matchesName(row, applied.name)),
        [releases, applied]
    );

    // O endpoint de bloqueados devolve a lista inteira (é pequena por natureza: só quem está
    // no prazo da penalidade), então a paginação da grade é feita aqui.
    const blockedPage = useMemo(() => {
        const total = filteredBlocked.length;
        const totalPages = Math.max(Math.ceil(total / size), 1);
        const number = Math.min(page, totalPages - 1);
        const content = filteredBlocked.slice(number * size, number * size + size);
        return {
            content,
            pageable: {
                number,
                size,
                totalElements: total,
                totalPages,
                numberOfElements: content.length,
                first: number === 0,
                last: number >= totalPages - 1
            }
        };
    }, [filteredBlocked, page, size]);

    const rows = mode === MODE_BLOCKED ? blockedPage.content : filteredReleases;
    const gridPageable = mode === MODE_BLOCKED ? blockedPage.pageable : pageable;

    const summaryCards = useMemo(() => {
        if (mode === MODE_RELEASES) {
            return [
                {
                    key: "releases",
                    className: "block-list__metric-card--active",
                    icon: "fa-light fa-lock-open",
                    label: t("online_release_history"),
                    value: pageable?.totalElements ?? releases.length,
                    caption: t("period")
                }
            ];
        }
        const dayFromNow = moment().add(24, "hours");
        return [
            {
                key: "blocked",
                className: "block-list__metric-card--active",
                icon: "fa-light fa-lock",
                label: t("blocked_now"),
                value: filteredBlocked.length,
                caption: t("blocked_customers")
            },
            {
                key: "no_show",
                className: "block-list__metric-card--other",
                icon: "fa-light fa-user-xmark",
                label: t("blocked_by_no_show"),
                value: filteredBlocked.filter((row) => row.type === TYPE_NO_SHOW).length,
                caption: t("blocked_now")
            },
            {
                key: "late_cancellation",
                className: "block-list__metric-card--hours",
                icon: "fa-light fa-calendar-xmark",
                label: t("blocked_by_late_cancellation"),
                value: filteredBlocked.filter((row) => row.type === TYPE_LATE_CANCELLATION).length,
                caption: t("blocked_now")
            },
            {
                key: "releasing_soon",
                className: "block-list__metric-card--total",
                icon: "fa-light fa-hourglass-half",
                label: t("releasing_in_24h"),
                value: filteredBlocked.filter((row) => moment(row.blockedUntil).isBefore(dayFromNow)).length,
                caption: t("blocked_now")
            }
        ];
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [mode, filteredBlocked, pageable, releases, t]);

    const releaseCustomer = (row) => {
        warnAlertWithReason(t("release_customer_confirm", { name: row.customerName }), {
            placeholder: t("release_reason_placeholder"),
            onConfirm: (notes) => {
                axiosInstance.post(`${Api.appointment.onlinePenalty}/${row.customerId}/release`, { notes })
                    .then(() => {
                        successAlert(t("customer_released"));
                        loadBlocked();
                    })
                    .catch((error) => errorObjectAlert(error));
            }
        });
    };

    const typeLabel = (type) => (type === TYPE_NO_SHOW ? t("blocked_by_no_show") : t("blocked_by_late_cancellation"));

    const formatDateTime = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "-");

    const emptyMessage = mode === MODE_BLOCKED ? t("no_blocked_customers") : t("no_releases_found");

    return (
        <>
            <PageTitle activeMenu={t("blocked_customers")} motherMenu={t("appointments")} />

            <FilterAccordion onSearch={handleSearch} onClear={handleClearFilters}>
                <Row className="g-3">
                    {/* Início e fim primeiro, como nas demais listagens; só o histórico tem período. */}
                    {mode === MODE_RELEASES && (
                        <>
                            <Col md={3}>
                                <FormLabel>{t("start")}</FormLabel>
                                <InputDate onChange={(value) => setStartDate(value)} selected={startDate} />
                            </Col>
                            <Col md={3}>
                                <FormLabel>{t("end")}</FormLabel>
                                <InputDate onChange={(value) => setEndDate(value)} selected={endDate} />
                            </Col>
                        </>
                    )}
                    <Col md={3}>
                        <FormLabel>{t("customer")}</FormLabel>
                        <InputText
                            value={nameFilter}
                            onChange={(event) => setNameFilter(event.target.value)}
                            placeholder={t("customer_name_or_phone")}
                        />
                    </Col>
                    {mode === MODE_BLOCKED && (
                        <Col md={3}>
                            <FormLabel>{t("reason")}</FormLabel>
                            <CustomSelect
                                value={typeFilter}
                                options={typeOptions}
                                onChange={(option) => setTypeFilter(option)}
                                placeholder={t("all")}
                                isClearable
                            />
                        </Col>
                    )}
                    <Col md={3}>
                        <FormLabel>{t("view")}</FormLabel>
                        <SegmentedButtons
                            compact
                            options={modeOptions}
                            value={mode}
                            onChange={handleModeChange}
                            ariaLabel={t("view")}
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            <Alert variant="info" className="py-2">
                <i className="fa-solid fa-circle-info me-2"></i>
                {t("blocked_customers_hint")}
            </Alert>

            <div className="block-list__metrics">
                {summaryCards.map((card) => (
                    <div key={card.key} className={`block-list__metric-card ${card.className}`}>
                        <div className="block-list__metric-head">
                            <span className="block-list__metric-label">{card.label}</span>
                            <span className="block-list__metric-icon">
                                <i className={card.icon}></i>
                            </span>
                        </div>
                        <p className="block-list__metric-value">{showLoading ? "-" : card.value}</p>
                        <p className="block-list__metric-caption">{card.caption}</p>
                    </div>
                ))}
            </div>

            <GridCustomBase
                pageable={gridPageable}
                setPage={setPage}
                setSize={setSize}
                showLoading={showLoading}
            >
                <table className="table table-list i-table style-1 mb-0 border-0">
                    <thead>
                        {mode === MODE_BLOCKED ? (
                            <tr>
                                <th>{t("customer")}</th>
                                <th>{t("reason")}</th>
                                <th className="block-list__col--optional">{t("branch")}</th>
                                <th>{t("blocked_until")}</th>
                                <th>{t("actions")}</th>
                            </tr>
                        ) : (
                            <tr>
                                <th>{t("customer")}</th>
                                <th>{t("released_at")}</th>
                                <th>{t("released_by")}</th>
                                <th className="block-list__col--optional">{t("notes")}</th>
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {rows.length === 0 && !showLoading && (
                            <tr>
                                <td colSpan={mode === MODE_BLOCKED ? 5 : 4} className="block-list__empty">
                                    {emptyMessage}
                                </td>
                            </tr>
                        )}
                        {mode === MODE_BLOCKED && rows.map((row) => (
                            <tr key={row.customerId}>
                                <td>
                                    <span className="block-list__cell-main">{row.customerName || "-"}</span>
                                    <span className="block-list__cell-detail">{row.customerPhone || "-"}</span>
                                    {/* No celular a coluna de filial sai da grade e vem para baixo do nome. */}
                                    {row.branchName && (
                                        <span className="block-list__cell-detail block-list__cell-detail--mobile">
                                            {row.branchName}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    <span className="block-list__cell-main">{typeLabel(row.type)}</span>
                                    <span className="block-list__cell-detail">
                                        {t("blocked_occurrence", { date: formatDateTime(row.occurrenceAt) })}
                                    </span>
                                </td>
                                <td className="block-list__col--optional">{row.branchName || "-"}</td>
                                <td>{formatDateTime(row.blockedUntil)}</td>
                                <td>
                                    <Button
                                        type="button"
                                        variant="light"
                                        size="sm"
                                        title={t("release_customer")}
                                        onClick={() => releaseCustomer(row)}
                                    >
                                        <i className="fa-light fa-lock-open me-1"></i>
                                        {t("release_customer")}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {mode === MODE_RELEASES && rows.map((row) => (
                            <tr key={row.id}>
                                <td>
                                    <span className="block-list__cell-main">{row.customerName || "-"}</span>
                                    <span className="block-list__cell-detail">{row.customerPhone || "-"}</span>
                                    {row.notes && (
                                        <span className="block-list__cell-detail block-list__cell-detail--mobile">
                                            {row.notes}
                                        </span>
                                    )}
                                </td>
                                <td>{formatDateTime(row.releasedAt)}</td>
                                <td>{row.releasedBy || "-"}</td>
                                <td className="block-list__col--optional">{row.notes || "-"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </GridCustomBase>
        </>
    );
};

export default BlockedCustomerList;
